// graders.mjs — THE one grader module (Task #485: unlimited gradable widgets).
// ---------------------------------------------------------------------------
// Single source of truth for skill-check grading, executed by BOTH runtimes:
//   * the browser (practice feedback + the server-graded predicate in
//     TutorStageController), bundled by esbuild like any other module;
//   * the server (`/api/learn/grade` → brilliant_courses.grade_beat → a short
//     Node runner, scripts/learn_grade_runner.mjs).
//
// THE LAW: a widget type is gradable if and only if it has an entry in the
// GRADERS table below. There is no other registry — eligibility, public-payload
// answer stripping, validator coverage, and the authored-TRUE/perturbed-FALSE
// harness are ALL derived from this table. Adding a new gradable widget is one
// JS change: its renderer (FractionWidgets.js) + its entry here, authored
// together. Nothing in Python, the validator, or the docs enumerates types.
//
// Each entry provides:
//   grade(spec, value) -> { correct: bool, answer: <reveal value> }
//       The authoritative verdict. `spec` is the TRUSTED authored widget from
//       the catalog (never the request); `value` is the student's submission.
//   secrets: [ { keys: [..], when?: { key, in: [..] } } ]
//       Declarative strip rules — the keys that must never ship in a public
//       skill-check payload. Pure data: serialized into the derived manifest
//       (static/data/learn_grader_manifest.json) that Python's _public_beats
//       consumes, and applied directly by stripSecrets() in JS consumers.
//   buildCorrect(spec) -> { value } | null
//       A submission the grader MUST accept (self-test). null = cannot build
//       one (an authoring failure the harness reports loudly).
//   buildWrong(spec, correct) -> { value } | null
//       A submission the grader MUST reject, or null when no perturbation is
//       provably wrong (e.g. tiles modes where many answers are valid).
//
// PYTHON-PARITY DISCIPLINE: these graders replaced the hand-mirrored Python
// `_grade_*` bodies in brilliant_courses.py. Every quirk of those graders is
// preserved deliberately (int-then-string compare, fail-closed int parses,
// computed compare winner, tangentSeek's central difference, deltaSeek's
// ε/|slope|, numFill's answerNum fallback…). The replay corpus
// (tests/learn/replay/ + scripts/check_grading_replay.mjs) pins every verdict
// the retired Python graders ever produced — run it after ANY edit here.
// pyInt/pyFloat/pyStr below mirror Python's int()/float()/str() coercion so
// those verdicts stay bit-identical. Do not "simplify" them to Number().
//
// This module must stay DOM-free, network-free, and deterministic: it runs
// under plain Node (the server verdict path) as well as in the browser bundle.
// ---------------------------------------------------------------------------

import {
  gradeTiles, gradeArea, areaRationals, compareWinner, optionValue,
  _compareOptions, parseFrac, slotCount, normCell, reduceFrac,
  isRectangleSelection,
} from "./FractionGrading.js";

// Re-export the whole shared grading core so graders.mjs is a one-stop import
// surface; existing importers of FractionGrading.js keep working unchanged.
export {
  gradeTiles, gradeArea, areaRationals, compareWinner, optionValue,
  _compareOptions, parseFrac, slotCount, normCell, reduceFrac,
  isRectangleSelection,
};

/* ── Python-coercion mirrors ──────────────────────────────────────────────
 * The retired Python graders used int()/float()/str() on submissions. Their
 * exact acceptance/rejection behavior IS the grading contract (a submission
 * of "3.5" to a pick fails; a submission of 3.5 truncates to 3), so we mirror
 * it rather than using JS Number() semantics. Throws PyCoerceError exactly
 * where Python raised ValueError/TypeError. */

class PyCoerceError extends Error {}

function pyInt(x) {
  // Mirror Python int(x) for JSON-representable inputs.
  if (typeof x === "boolean") return x ? 1 : 0;
  if (typeof x === "number") {
    // int(float) truncates toward zero; int(nan/inf) raises.
    if (!Number.isFinite(x)) throw new PyCoerceError("non-finite");
    return Math.trunc(x);
  }
  if (typeof x === "string") {
    const s = x.trim();
    if (!/^[+-]?\d+(_\d+)*$/.test(s)) throw new PyCoerceError(`int('${x}')`);
    return parseInt(s.replace(/_/g, ""), 10);
  }
  throw new PyCoerceError("int() argument");
}

function pyFloat(x) {
  // Mirror Python float(x) for JSON-representable inputs.
  if (typeof x === "boolean") return x ? 1 : 0;
  if (typeof x === "number") return x;
  if (typeof x === "string") {
    const s = x.trim().replace(/_/g, "");
    if (/^[+-]?inf(inity)?$/i.test(s)) return s[0] === "-" ? -Infinity : Infinity;
    if (/^[+-]?nan$/i.test(s)) return NaN;
    if (!/^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(s)) {
      throw new PyCoerceError(`float('${x}')`);
    }
    return Number(s);
  }
  throw new PyCoerceError("float() argument");
}

function pyRepr(x) {
  // Python repr() for JSON values — used inside container str() forms.
  if (x === null || x === undefined) return "None";
  if (x === true) return "True";
  if (x === false) return "False";
  if (typeof x === "string") return "'" + x.replace(/\\/g, "\\\\").replace(/'/g, "\\'") + "'";
  if (Array.isArray(x)) return "[" + x.map(pyRepr).join(", ") + "]";
  if (typeof x === "object") {
    return "{" + Object.entries(x).map(([k, v]) => pyRepr(k) + ": " + pyRepr(v)).join(", ") + "}";
  }
  return String(x);
}

function pyStr(x) {
  // Mirror Python str() for the JSON values the numberTile fallback sees.
  // Containers matter: str([1]) is "[1]" in Python but String([1]) is "1" in
  // JS — without this, a list submission [1] would wrongly match answer 1.
  if (x === null || x === undefined) return "None";
  if (x === true) return "True";
  if (x === false) return "False";
  if (Array.isArray(x) || typeof x === "object") return pyRepr(x);
  // Numbers: JSON's int/float blur makes str(2.0) unmirrorable (JS sees 2);
  // verdict-neutral for real content (int answers take the int path).
  return String(x);
}

function pyAreaIndices(value) {
  // Python-parity shim for shade/overlapGrid submissions: the legacy
  // _grade_area coerced each index with int(float(x)) under try/except, so
  // None/""/non-numeric strings REJECT the whole submission. JS Number()
  // would quietly turn null/"" into index 0 — that must not become a verdict.
  if (!Array.isArray(value)) return null;
  const out = [];
  for (const raw of value) {
    let f;
    try {
      f = pyFloat(raw);
    } catch (_) {
      return null;
    }
    if (!Number.isFinite(f)) return null;
    out.push(Math.trunc(f));
  }
  return out;
}

// Python list sort on [x, y] sample pairs (elementwise numeric compare).
function cmpPair(a, b) {
  const la = Array.isArray(a) ? a : [a];
  const lb = Array.isArray(b) ? b : [b];
  const n = Math.min(la.length, lb.length);
  for (let i = 0; i < n; i++) {
    if (la[i] < lb[i]) return -1;
    if (la[i] > lb[i]) return 1;
  }
  return la.length - lb.length;
}

const numSort = (a, b) => a - b;
const missing = (v) => v === null || v === undefined; // dict.get(...) is None

/* ── per-type grading bodies (ports of the retired Python graders) ──────── */

function gradeSignedTiles(w, value) {
  // Submission = list of slot NET values. grade:"slots" → per-slot equality
  // with `answer`; grade:"sum" → every slot filled and nets total `answerSum`.
  const eq = Array.isArray(w.equation) ? w.equation : [];
  const slots = eq.filter((p) => p && typeof p === "object" && !Array.isArray(p) && p.slot);
  let vals = Array.isArray(value) ? value : [value];
  try {
    vals = vals.map(pyInt);
  } catch (_) {
    return false;
  }
  if (vals.length !== slots.length) return false;
  if (w.grade === "sum") {
    // NOTE: like the Python original, a malformed authored answerSum throws
    // (fail loud, not wrong) — the spec is trusted catalog content.
    return !missing(w.answerSum) && vals.reduce((a, b) => a + b, 0) === pyInt(w.answerSum);
  }
  const ans = Array.isArray(w.answer) ? w.answer : [w.answer];
  let ansInts;
  try {
    ansInts = ans.map(pyInt);
  } catch (_) {
    return false;
  }
  return ansInts.length === vals.length && ansInts.every((a, i) => a === vals[i]);
}

function gradeIntTiles(w, value) {
  // Submission = list of blank values in blank order. grade:"exact" is an
  // order-insensitive multiset; grade:"sum" totals `answerSum`.
  const blanks = String(w.template || "?").split("?").length - 1;
  let vals = Array.isArray(value) ? value : [value];
  try {
    vals = vals.map(pyInt);
  } catch (_) {
    return false;
  }
  if (vals.length !== blanks) return false;
  if (w.grade === "sum") {
    return !missing(w.answerSum) && vals.reduce((a, b) => a + b, 0) === pyInt(w.answerSum);
  }
  const ans = Array.isArray(w.answer) ? w.answer : [w.answer];
  let ansInts;
  try {
    ansInts = ans.map(pyInt);
  } catch (_) {
    return false;
  }
  const a = ansInts.slice().sort(numSort);
  const v = vals.slice().sort(numSort);
  return a.length === v.length && a.every((x, i) => x === v[i]);
}

function gradeSlopeFit(w, value) {
  let v, ans;
  try {
    v = pyFloat(value);
  } catch (_) {
    return false;
  }
  try {
    ans = pyFloat(w.answer);
  } catch (_) {
    return false;
  }
  return Math.abs(v - ans) < 1e-9;
}

function gradeTangentSeek(w, value) {
  // Submission = handle X; recompute the slope from graph.curve.samples
  // (central difference) and accept within `tolerance` of `target`.
  let x;
  try {
    x = pyFloat(value);
  } catch (_) {
    return false;
  }
  const samples = (((w.graph || {}).curve) || {}).samples || [];
  const pts = samples.slice().sort(cmpPair);
  if (pts.length < 3) return false;
  const first = pts[0], last = pts[pts.length - 1];
  const f = (q) => {
    q = Math.min(Math.max(q, first[0]), last[0]);
    for (let i = 1; i < pts.length; i++) {
      if (q <= pts[i][0]) {
        const x0 = pts[i - 1][0], y0 = pts[i - 1][1];
        const x1 = pts[i][0], y1 = pts[i][1];
        const t = x1 === x0 ? 0 : (q - x0) / (x1 - x0);
        return y0 + t * (y1 - y0);
      }
    }
    return last[1];
  };
  const h = (last[0] - first[0]) / 200.0;
  if (h === 0) return false;
  const slope = (f(x + h) - f(x - h)) / (2 * h);
  if (!("target" in w)) return false; // Python KeyError → False
  let target;
  try {
    target = pyFloat(w.target);
  } catch (_) {
    return false;
  }
  // Like Python, a malformed authored tolerance throws (trusted spec).
  const tol = pyFloat(w.tolerance === undefined ? 0.05 : w.tolerance);
  return Math.abs(slope - target) <= tol;
}

function gradePointSelect(w, value) {
  // Exact sorted index-SET equality; answer/submission may be scalar or list.
  const answer = w.answer;
  if (missing(answer)) return false;
  const want = Array.isArray(answer) ? answer : [answer];
  const got = Array.isArray(value) ? value : [value];
  let ws, gs;
  try {
    ws = want.map(pyInt).sort(numSort);
    gs = got.map(pyInt).sort(numSort);
  } catch (_) {
    return false;
  }
  return ws.length === gs.length && ws.every((v, i) => v === gs[i]);
}

function gradeNumFill(w, value) {
  // |value - answerNum| <= tolerance; answerNum falls back to `answer`.
  let v;
  try {
    v = pyFloat(value);
  } catch (_) {
    return false;
  }
  let raw = w.answerNum;
  if (missing(raw)) raw = w.answer;
  let ans;
  try {
    ans = pyFloat(raw);
  } catch (_) {
    return false;
  }
  let tol;
  try {
    tol = pyFloat(w.tolerance === undefined ? 1e-9 : w.tolerance);
  } catch (_) {
    tol = 1e-9;
  }
  return Math.abs(v - ans) <= tol;
}

function gradeDeltaSeek(w, value) {
  // Correct δ is RECOMPUTED as ε/|slope| (never authored); accepted within
  // `tolerance`, defaulting to max(0.08, target*0.12) like the client.
  let v;
  try {
    v = pyFloat(value);
  } catch (_) {
    return false;
  }
  let eps, m;
  try {
    eps = pyFloat(w.epsilon === undefined ? 1 : w.epsilon);
    m = Math.abs(pyFloat(w.slope === undefined ? 1 : w.slope));
  } catch (_) {
    return false;
  }
  if (m === 0) return false;
  const target = eps / m;
  let tol;
  try {
    tol = !missing(w.tolerance) ? pyFloat(w.tolerance) : Math.max(0.08, target * 0.12);
  } catch (_) {
    tol = Math.max(0.08, target * 0.12);
  }
  return Math.abs(v - target) <= tol;
}

function deltaSeekAnswer(w) {
  // The reveal target for deltaSeek = ε/|slope| (computed, never shipped).
  let eps, m;
  try {
    eps = pyFloat(w.epsilon === undefined ? 1 : w.epsilon);
    m = Math.abs(pyFloat(w.slope === undefined ? 1 : w.slope));
  } catch (_) {
    return null;
  }
  return m === 0 ? null : eps / m;
}

function gradePickLike(w, value) {
  // Option/region INDEX vs authored `answer`. Fail-closed: no string fallback.
  const answer = w.answer;
  let correct = false;
  try {
    correct = !missing(answer) && pyInt(value) === pyInt(answer);
  } catch (_) {
    correct = false;
  }
  return { correct: !!correct, answer: missing(answer) ? null : answer };
}

function gradeNumberTile(w, value) {
  // Int compare with a trimmed-string fallback (the one type that has it).
  const answer = w.answer;
  let correct = false;
  try {
    correct = !missing(answer) && pyInt(value) === pyInt(answer);
  } catch (_) {
    correct = pyStr(value).trim() === pyStr(missing(answer) ? null : answer).trim();
  }
  return { correct: !!correct, answer: missing(answer) ? null : answer };
}

/* ── self-test helpers (shared) ──────────────────────────────────────────── */

function gcdInt(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b) { const t = a % b; a = b; b = t; }
  return a || 1;
}

function ratKey(r) {
  let [n, d] = r;
  if (d < 0) { n = -n; d = -d; }
  const g = gcdInt(n, d);
  return `${n / g}/${d / g}`;
}

function ratLeq(a, b) {
  // a <= b with positive denominators (area rationals are positive).
  const [an, ad] = a, [bn, bd] = b;
  const sa = ad < 0 ? -1 : 1, sb = bd < 0 ? -1 : 1;
  return an * sa * Math.abs(bd) <= bn * sb * Math.abs(ad);
}

function targetFraction(w) {
  const t = w.target;
  if (typeof t === "string" && t.includes("/")) {
    const parts = t.split("/");
    if (parts.length !== 2) return null;
    const a = parseInt(parts[0].trim(), 10);
    const b = parseInt(parts[1].trim(), 10);
    if (!Number.isFinite(a) || !Number.isFinite(b) || !b) return null;
    return [a, b];
  }
  const n = typeof t === "number" ? t : Number(t);
  if (!Number.isFinite(n)) return null;
  if (Number.isInteger(n)) return [n, 1];
  for (let d = 2; d <= 1000000; d++) {
    const p = Math.round(n * d);
    if (Math.abs(p / d - n) < 1e-9) return [p, d];
  }
  return null;
}

function buildAreaSelection(w) {
  // Exact subset of cell/region indices whose areas sum to the target.
  const tv = targetFraction(w);
  if (!tv) return null;
  if (w.type === "overlapGrid") {
    const rows = (w.grid || {}).rows || w.rows;
    const cols = (w.grid || {}).cols || w.cols;
    const n = pyIntOrNull(rows) * pyIntOrNull(cols);
    if (!Number.isFinite(n) || n <= 0) return null;
    const k = Math.round((tv[0] * n) / tv[1]);
    const out = [];
    for (let i = 0; i < k; i++) out.push(i);
    return out;
  }
  const rats = areaRationals(w);
  if (!rats) return null;
  // DP subset-sum over exact rationals: reachable sum -> first index list.
  const reach = new Map([[ratKey([0, 1]), { sum: [0, 1], idxs: [] }]]);
  rats.forEach((a, i) => {
    for (const { sum, idxs } of Array.from(reach.values())) {
      const ns = [sum[0] * a[1] + a[0] * sum[1], sum[1] * a[1]];
      const k = ratKey(ns);
      if (ratLeq(ns, tv) && !reach.has(k)) {
        reach.set(k, { sum: ns, idxs: idxs.concat([i]) });
      }
    }
  });
  const hit = reach.get(ratKey(tv));
  return hit ? hit.idxs : null;
}

function pyIntOrNull(x) {
  try {
    return pyInt(x);
  } catch (_) {
    return NaN;
  }
}

// Tiles grade modes where MANY submissions are legitimately correct — a naive
// perturbation of the authored answer is not necessarily wrong.
const TILES_MULTI_VALID = new Set(["value", "equivalent", "asWritten", "sum", "product", "expr"]);

function listOrWrap(v) {
  return Array.isArray(v) ? v : (missing(v) ? null : [v]);
}

/* ── THE TABLE ────────────────────────────────────────────────────────────
 * One entry per gradable widget type. This is the open-ended registry that
 * "gradable" means. Renderer + entry ship together; nothing else changes. */

// Baseline secret keys shared by every gradable type (the generic strip the
// server always applied). Type entries add their extras on top.
/* ── orderTiles (Task #485's headline widget — born as ONE JS change) ────── */

// Numeric value per item, or null when any item is unparsable / fewer than 2.
// Items may be numbers, "a/b" strings, plain numeric strings, or
// { label, value } objects (label renders, value grades).
function orderTilesValues(w) {
  const items = Array.isArray(w && w.items) ? w.items : [];
  if (items.length < 2) return null;
  const vals = [];
  for (const it of items) {
    const raw = it && typeof it === "object" ? it.value : it;
    let v = NaN;
    if (typeof raw === "number") v = raw;
    else if (typeof raw === "string") {
      const m = raw.trim().match(/^(-?\d+(?:\.\d+)?)\s*\/\s*(-?\d+(?:\.\d+)?)$/);
      if (m) {
        const d = parseFloat(m[2]);
        v = d === 0 ? NaN : parseFloat(m[1]) / d;
      } else {
        v = parseFloat(raw);
      }
    }
    if (!Number.isFinite(v)) return null;
    vals.push(v);
  }
  return vals;
}

// Submission = a FULL permutation of item indices in reading order. Correct
// iff the arranged values are monotone per `order` ("asc" default | "desc");
// ties may sit either way. Anything short of a real permutation is wrong.
// Strict index parse for orderTiles submissions: a finite integer (or an
// integer-string like "2"), else null. Deliberately NOT pyIntOrNull — that
// helper mirrors Python int() for the ported graders and returns NaN on
// garbage / truncates floats, which would let malformed indices slip through
// bounds checks (NaN < 0 is false). orderTiles is new, so it takes the
// strict contract: a full permutation of REAL indices or the answer is wrong.
function orderIndexOrNull(x) {
  if (Number.isInteger(x)) return x;
  if (typeof x === "string" && /^-?\d+$/.test(x.trim())) return parseInt(x.trim(), 10);
  return null;
}

function gradeOrderTiles(w, value) {
  const vals = orderTilesValues(w);
  if (!vals) return false;
  if (!Array.isArray(value) || value.length !== vals.length) return false;
  const seen = new Set();
  const idxs = [];
  for (const x of value) {
    const i = orderIndexOrNull(x);
    if (i === null || i < 0 || i >= vals.length || seen.has(i)) return false;
    seen.add(i);
    idxs.push(i);
  }
  const desc = String(w.order || "asc").toLowerCase() === "desc";
  for (let k = 0; k + 1 < idxs.length; k++) {
    const a = vals[idxs[k]], b = vals[idxs[k + 1]];
    if (desc ? a < b : a > b) return false;
  }
  return true;
}

// Canonical permutation for reveal(): the authored `answer` when it actually
// grades correct, else a stable sort of the indices by value. Computable from
// the PUBLIC items alone, so reveal works from a stripped payload too.
function orderTilesAnswer(w) {
  if (Array.isArray(w && w.answer) && gradeOrderTiles(w, w.answer)) {
    return w.answer.map((x) => orderIndexOrNull(x));
  }
  const vals = orderTilesValues(w);
  if (!vals) return null;
  const desc = String(w.order || "asc").toLowerCase() === "desc";
  const idx = vals.map((_, i) => i);
  idx.sort((a, b) => (desc ? vals[b] - vals[a] : vals[a] - vals[b]) || a - b);
  return idx;
}

const BASE_SECRETS = { keys: ["answer", "answerAlts"] };

export const GRADERS = {
  numberTile: {
    grade: (w, value) => gradeNumberTile(w, value),
    secrets: [BASE_SECRETS],
    buildCorrect: (w) => (missing(w.answer) ? null : { value: w.answer }),
    buildWrong: (w, correct) => {
      try {
        return { value: pyInt(correct) + 1 };
      } catch (_) {
        return null;
      }
    },
  },

  tiles: {
    grade: (w, value) => ({
      correct: !!gradeTiles(w, value),
      answer: missing(w.answer) ? null : w.answer,
    }),
    secrets: [
      BASE_SECRETS,
      // For value/equivalent/asWritten the answer key can live ENTIRELY in
      // `target` (and `different`) — strip those too, exactly as the server
      // always did. buildTiles only reads target in getState, never to render.
      { keys: ["target", "different"], when: { key: "grade", in: ["value", "equivalent", "asWritten"] } },
    ],
    buildCorrect: (w) => {
      let ans = w.answer;
      if (missing(ans) && Array.isArray(w.answerAlts) && w.answerAlts.length) {
        ans = w.answerAlts[0];
      }
      return missing(ans) ? null : { value: ans };
    },
    buildWrong: (w, correct) => {
      if (TILES_MULTI_VALID.has(w.grade)) return null;
      if (!Array.isArray(correct) || !correct.length) return null;
      const cand = [correct[0] + 1].concat(correct.slice(1));
      const valid = [w.answer].concat(Array.isArray(w.answerAlts) ? w.answerAlts : []);
      const same = (a) => Array.isArray(a) && a.length === cand.length && a.every((x, i) => x === cand[i]);
      return valid.some(same) ? null : { value: cand };
    },
  },

  pick: {
    grade: gradePickLike,
    secrets: [BASE_SECRETS],
    buildCorrect: (w) => (missing(w.answer) ? null : { value: w.answer }),
    buildWrong: (w, correct) => {
      try {
        return { value: pyInt(correct) + 1 };
      } catch (_) {
        return null;
      }
    },
  },

  shade: {
    grade: (w, value) => {
      const idx = pyAreaIndices(value);
      return {
        correct: idx !== null && !!gradeArea(w, idx),
        answer: missing(w.target) ? null : w.target,
      };
    },
    secrets: [BASE_SECRETS, { keys: ["target"] }],
    buildCorrect: (w) => {
      const sel = buildAreaSelection(w);
      return sel ? { value: sel } : null;
    },
    buildWrong: (w, correct) =>
      (Array.isArray(correct) && correct.length > 1 ? { value: correct.slice(0, -1) } : null),
  },

  overlapGrid: {
    grade: (w, value) => {
      const idx = pyAreaIndices(value);
      return {
        correct: idx !== null && !!gradeArea(w, idx),
        answer: missing(w.target) ? null : w.target,
      };
    },
    secrets: [BASE_SECRETS, { keys: ["target"] }],
    buildCorrect: (w) => {
      const sel = buildAreaSelection(w);
      return sel ? { value: sel } : null;
    },
    buildWrong: (w, correct) =>
      (Array.isArray(correct) && correct.length > 1 ? { value: correct.slice(0, -1) } : null),
  },

  compare: {
    grade: (w, value) => {
      // The winner is COMPUTED from the option values (never authored).
      const winner = compareWinner(w);
      let correct = false;
      try {
        correct = winner >= 0 && pyInt(value) === winner;
      } catch (_) {
        correct = false;
      }
      return { correct: !!correct, answer: winner };
    },
    secrets: [BASE_SECRETS],
    buildCorrect: (w) => {
      const winner = compareWinner(w);
      return winner >= 0 ? { value: winner } : null;
    },
    buildWrong: (w, correct) => {
      const opts = _compareOptions(w);
      if (opts.length < 2) return null;
      return { value: (pyIntOrNull(correct) + 1) % opts.length };
    },
  },

  signedTiles: {
    grade: (w, value) => ({
      correct: !!gradeSignedTiles(w, value),
      answer: missing(w.answer) ? null : w.answer,
    }),
    secrets: [BASE_SECRETS, { keys: ["answerSum"] }],
    buildCorrect: (w) => {
      if (w.grade === "sum") {
        if (missing(w.answerSum)) return null;
        const eq = Array.isArray(w.equation) ? w.equation : [];
        const slots = eq.filter((p) => p && typeof p === "object" && !Array.isArray(p) && p.slot).length;
        if (slots < 1) return null;
        const sum = pyIntOrNull(w.answerSum);
        if (!Number.isFinite(sum)) return null;
        const v = new Array(slots).fill(0);
        v[0] = sum;
        return { value: v };
      }
      const ans = listOrWrap(w.answer);
      return ans ? { value: ans } : null;
    },
    buildWrong: (w, correct) => {
      if (!Array.isArray(correct) || !correct.length) return null;
      const first = pyIntOrNull(correct[0]);
      if (!Number.isFinite(first)) return null;
      // slots mode: per-slot equality breaks; sum mode: the total is off by 1.
      return { value: [first + 1].concat(correct.slice(1)) };
    },
  },

  intTiles: {
    grade: (w, value) => ({
      correct: !!gradeIntTiles(w, value),
      answer: missing(w.answer) ? null : w.answer,
    }),
    secrets: [BASE_SECRETS, { keys: ["answerSum"] }],
    buildCorrect: (w) => {
      if (w.grade === "sum") {
        if (missing(w.answerSum)) return null;
        const blanks = String(w.template || "?").split("?").length - 1;
        if (blanks < 1) return null;
        const sum = pyIntOrNull(w.answerSum);
        if (!Number.isFinite(sum)) return null;
        const v = new Array(blanks).fill(0);
        v[0] = sum;
        return { value: v };
      }
      const ans = listOrWrap(w.answer);
      return ans ? { value: ans } : null;
    },
    buildWrong: (w, correct) => {
      if (!Array.isArray(correct) || !correct.length) return null;
      const first = pyIntOrNull(correct[0]);
      if (!Number.isFinite(first)) return null;
      // exact mode: the multiset changes (c0 → c0+1); sum mode: total off by 1.
      return { value: [first + 1].concat(correct.slice(1)) };
    },
  },

  graphTrace: {
    grade: gradePickLike,
    secrets: [BASE_SECRETS],
    buildCorrect: (w) => (missing(w.answer) ? null : { value: w.answer }),
    buildWrong: (w, correct) => {
      try {
        return { value: pyInt(correct) + 1 };
      } catch (_) {
        return null;
      }
    },
  },

  curveTrace: {
    grade: gradePickLike,
    secrets: [BASE_SECRETS],
    buildCorrect: (w) => (missing(w.answer) ? null : { value: w.answer }),
    buildWrong: (w, correct) => {
      try {
        return { value: pyInt(correct) + 1 };
      } catch (_) {
        return null;
      }
    },
  },

  regionPick: {
    grade: gradePickLike,
    secrets: [BASE_SECRETS],
    buildCorrect: (w) => (missing(w.answer) ? null : { value: w.answer }),
    buildWrong: (w, correct) => {
      try {
        return { value: pyInt(correct) + 1 };
      } catch (_) {
        return null;
      }
    },
  },

  slopeFit: {
    grade: (w, value) => ({
      correct: !!gradeSlopeFit(w, value),
      answer: missing(w.answer) ? null : w.answer,
    }),
    secrets: [BASE_SECRETS],
    buildCorrect: (w) => (missing(w.answer) ? null : { value: w.answer }),
    buildWrong: (w, correct) => {
      try {
        return { value: pyFloat(correct) + 1 };
      } catch (_) {
        return null;
      }
    },
  },

  tangentSeek: {
    grade: (w, value) => ({
      correct: !!gradeTangentSeek(w, value),
      // reveal moves the point to answerX; the verdict is recomputed from the
      // curve, so answerX is what the client's reveal() uses on a miss.
      answer: missing(w.answerX) ? null : w.answerX,
    }),
    secrets: [BASE_SECRETS, { keys: ["answerX"] }],
    buildCorrect: (w) => (missing(w.answerX) ? null : { value: w.answerX }),
    buildWrong: (w, correct) => {
      // Candidates from the edges inward — but VERIFY each one actually
      // grades false: at a clamped sample edge the central difference can
      // flatten the measured slope back inside tolerance (a real, shipped
      // quirk of the grader — both engines agree), so "far from answerX"
      // does not imply "wrong". Null when the whole curve grades correct.
      const pts = ((((w.graph || {}).curve) || {}).samples || []).slice().sort(cmpPair);
      if (pts.length < 2) return null;
      const lo = pts[0][0], hi = pts[pts.length - 1][0];
      let cx;
      try {
        cx = pyFloat(correct);
      } catch (_) {
        return null;
      }
      const far = Math.abs(cx - hi) > Math.abs(cx - lo) ? lo : hi;
      const near = far === lo ? hi : lo;
      const cands = [far, near,
        lo + (hi - lo) * 0.25, lo + (hi - lo) * 0.5, lo + (hi - lo) * 0.75];
      for (const c of cands) {
        if (!gradeTangentSeek(w, c)) return { value: c };
      }
      return null;
    },
  },

  pointSelect: {
    grade: (w, value) => ({
      correct: !!gradePointSelect(w, value),
      answer: missing(w.answer) ? null : w.answer,
    }),
    secrets: [BASE_SECRETS],
    buildCorrect: (w) => {
      const ans = listOrWrap(w.answer);
      return ans ? { value: ans } : null;
    },
    buildWrong: (w, correct) => {
      if (!Array.isArray(correct) || !correct.length) return null;
      const n = Array.isArray(w.points) ? w.points.length : 0;
      const c0 = pyIntOrNull(correct[0]);
      if (!Number.isFinite(c0)) return null;
      const cand = correct.slice();
      cand[0] = (c0 + 1) % Math.max(n, c0 + 2);
      const sortedCand = cand.map(pyIntOrNull).sort(numSort);
      const sortedCorrect = correct.map(pyIntOrNull).sort(numSort);
      const same = sortedCand.length === sortedCorrect.length &&
        sortedCand.every((x, i) => x === sortedCorrect[i]);
      return same ? null : { value: cand };
    },
  },

  seqFill: {
    grade: (w, value) => {
      let ans = w.answerNum;
      if (missing(ans)) ans = w.answer;
      return {
        correct: !!gradeNumFill(w, value),
        answer: missing(ans) ? null : ans,
      };
    },
    secrets: [BASE_SECRETS, { keys: ["answerNum"] }],
    buildCorrect: (w) => {
      let ans = w.answerNum;
      if (missing(ans)) ans = w.answer;
      return missing(ans) ? null : { value: ans };
    },
    buildWrong: (w, correct) => {
      let tol;
      try {
        tol = pyFloat(w.tolerance === undefined ? 1e-9 : w.tolerance);
      } catch (_) {
        tol = 1e-9;
      }
      try {
        return { value: pyFloat(correct) + Math.max(1.0, tol * 10 + 1.0) };
      } catch (_) {
        return null;
      }
    },
  },

  numFill: {
    grade: (w, value) => {
      let ans = w.answerNum;
      if (missing(ans)) ans = w.answer;
      return {
        correct: !!gradeNumFill(w, value),
        answer: missing(ans) ? null : ans,
      };
    },
    secrets: [BASE_SECRETS, { keys: ["answerNum"] }],
    buildCorrect: (w) => {
      let ans = w.answerNum;
      if (missing(ans)) ans = w.answer;
      return missing(ans) ? null : { value: ans };
    },
    buildWrong: (w, correct) => {
      let tol;
      try {
        tol = pyFloat(w.tolerance === undefined ? 1e-9 : w.tolerance);
      } catch (_) {
        tol = 1e-9;
      }
      try {
        return { value: pyFloat(correct) + Math.max(1.0, tol * 10 + 1.0) };
      } catch (_) {
        return null;
      }
    },
  },

  deltaSeek: {
    grade: (w, value) => ({
      correct: !!gradeDeltaSeek(w, value),
      // The correct δ is RECOMPUTED (ε/|slope|, never authored) — that
      // computed value is what reveal() uses. Nothing secret ships.
      answer: deltaSeekAnswer(w),
    }),
    secrets: [BASE_SECRETS],
    buildCorrect: (w) => {
      const a = deltaSeekAnswer(w);
      return a === null ? null : { value: a };
    },
    buildWrong: (w, correct) => {
      let target;
      try {
        target = pyFloat(correct);
      } catch (_) {
        return null;
      }
      let tol;
      try {
        tol = !missing(w.tolerance) ? pyFloat(w.tolerance) : Math.max(0.08, target * 0.12);
      } catch (_) {
        tol = Math.max(0.08, target * 0.12);
      }
      return { value: target + tol * 4 + 1.0 };
    },
  },

  // orderTiles — arrange the given items into ascending/descending order.
  // Task #485's headline proof: this type reached production as ONE JS change
  // (this entry + its FractionWidgets builder) — zero Python, validator,
  // route, or doc edits. Spec:
  //   { type:"orderTiles", items:[...], order:"asc"|"desc", answer?:[i,...] }
  orderTiles: {
    grade: (w, value) => ({
      correct: gradeOrderTiles(w, value),
      answer: orderTilesAnswer(w),
    }),
    secrets: [BASE_SECRETS],
    buildCorrect: (w) => {
      const a = orderTilesAnswer(w);
      return a === null ? null : { value: a };
    },
    buildWrong: (w, correct) => {
      if (!Array.isArray(correct) || correct.length < 2) return null;
      const vals = orderTilesValues(w);
      if (!vals) return null;
      // Swap the first adjacent pair with UNEQUAL values (ties legitimately
      // sit either way), then VERIFY the swap actually grades false.
      for (let i = 0; i + 1 < correct.length; i++) {
        if (vals[correct[i]] !== vals[correct[i + 1]]) {
          const cand = correct.slice();
          const t = cand[i];
          cand[i] = cand[i + 1];
          cand[i + 1] = t;
          if (!gradeOrderTiles(w, cand)) return { value: cand };
        }
      }
      return null; // all values equal — every arrangement is correct
    },
  },
};

/* ── public API (everything consumers derive from the table) ─────────────── */

export function isGradable(type) {
  return typeof type === "string" && Object.prototype.hasOwnProperty.call(GRADERS, type);
}

export function gradableTypes() {
  return Object.keys(GRADERS);
}

/**
 * Authoritative verdict for one widget spec + submission.
 * Returns { correct, answer } or null when the type has no grader (the caller
 * 404s — "not gradable" must never silently become a verdict).
 */
export function gradeWidget(spec, value) {
  if (!spec || typeof spec !== "object" || !isGradable(spec.type)) return null;
  const v = GRADERS[spec.type].grade(spec, value);
  return {
    correct: !!(v && v.correct),
    answer: v && v.answer !== undefined ? v.answer : null,
  };
}

/** Self-test: a submission the grader must accept, or null. */
export function buildCorrectSubmission(spec) {
  if (!spec || typeof spec !== "object" || !isGradable(spec.type)) return null;
  try {
    return GRADERS[spec.type].buildCorrect(spec) || null;
  } catch (_) {
    return null;
  }
}

/** Self-test: a submission the grader must reject, or null (skip). */
export function buildWrongSubmission(spec, correct) {
  if (!spec || typeof spec !== "object" || !isGradable(spec.type)) return null;
  try {
    return GRADERS[spec.type].buildWrong(spec, correct) || null;
  } catch (_) {
    return null;
  }
}

function whenMatches(widget, when) {
  if (!when || typeof when !== "object") return true;
  const list = Array.isArray(when.in) ? when.in : [];
  return list.includes(widget[when.key]);
}

/**
 * Delete a gradable widget's secret keys IN PLACE (callers pass a copy).
 * Mirrors the manifest-driven strip the server applies in _public_beats —
 * the two interpret the SAME declarative rules.
 */
export function stripSecrets(widget) {
  if (!widget || typeof widget !== "object" || !isGradable(widget.type)) return widget;
  for (const rule of GRADERS[widget.type].secrets) {
    if (!whenMatches(widget, rule.when)) continue;
    for (const k of rule.keys) delete widget[k];
  }
  return widget;
}

/** All secret keys any grader declares (the server's fail-closed superset). */
export function allSecretKeys() {
  const keys = new Set();
  for (const entry of Object.values(GRADERS)) {
    for (const rule of entry.secrets) rule.keys.forEach((k) => keys.add(k));
  }
  return Array.from(keys).sort();
}

/**
 * Pure-data view of the table for the derived Python manifest
 * (scripts/generate_grader_manifest.mjs → static/data/learn_grader_manifest.json).
 */
export function graderManifestTypes() {
  const out = {};
  for (const [type, entry] of Object.entries(GRADERS)) {
    out[type] = { secrets: entry.secrets };
  }
  return out;
}
